import React, { useState } from 'react';
import { ArrowLeft, Check, ChevronRight, Info } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ConsentInfoSheet } from '../components/ui/ConsentInfoSheet';
import { SubscriptionPlan, formatCurrency } from '../data/subscriptionPlans';
import { useTheme } from '../contexts/ThemeContext';
interface SubscriptionConsentScreenProps {
  plan: SubscriptionPlan;
  hasTrialOffer?: boolean;
  onBack: () => void;
  onContinue: () => void;
}
const DISCLOSURES = ['Your AI identifies itself to callers where required.', 'Calls may be recorded or transcribed where the law allows.', 'You authorize eligible calls to be forwarded to Vexu.', 'Your carrier may charge standard rates for forwarded calls.'];
export function SubscriptionConsentScreen({
  plan,
  hasTrialOffer = true,
  onBack,
  onContinue
}: SubscriptionConsentScreenProps) {
  const {
    isLight
  } = useTheme();
  const [hasAgreed, setHasAgreed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const muted = isLight ? 'text-slate-500' : 'text-zinc-400';
  const strong = isLight ? 'text-slate-900' : 'text-white';
  const link = `font-semibold underline underline-offset-2 ${isLight ? 'text-vexu-700' : 'text-vexu'}`;
  return <div className={`relative flex h-full flex-col overflow-hidden ${isLight ? 'bg-slate-50' : 'bg-black'}`} data-id="element-3084" data-mp="d8ovofmitwh8">
      <header className={`flex items-center justify-between border-b px-5 py-4 ${isLight ? 'border-slate-200 bg-white' : 'border-white/10 bg-black'}`} data-id="element-3085" data-mp="8e1d0xe77wak">
        <button type="button" onClick={onBack} aria-label="Back to plans" className={`-ml-2 rounded-full p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'text-slate-500 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/10'}`} data-id="element-3086" data-mp="i9ctpl963dky">
          <ArrowLeft className="h-5 w-5" data-id="element-3087" data-mp="110chwk8u8jd6" />
        </button>
        <span className={`text-xs font-bold ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-3088" data-mp="111xba6jf1w9g">Review consent</span>
        <span className={`text-xs font-semibold ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-3089" data-mp="181hcvqq54px2">2 of 3</span>
      </header>

      <main className="flex-1 overflow-y-auto px-6 py-6" data-id="element-3090" data-mp="1i1qa97vvche1">
        <h1 className={`text-2xl font-bold tracking-tight ${strong}`} data-id="element-3091" data-mp="1i05fbh5n6wfn">Before we connect your calls</h1>
        <p className={`mt-2 text-sm leading-relaxed ${muted}`} data-id="element-3092" data-mp="1mtrkko9h7y1f">
          A quick agreement on how your AI assistant handles calls.
        </p>

        {/* Compact plan & trial summary */}
        <div className={`mt-5 flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${isLight ? 'border-slate-200 bg-white' : 'border-white/10 bg-white/5'}`} data-id="element-3093" data-mp="1vrk301ahvlt1">
          <div className="min-w-0" data-id="element-3094" data-mp="25pzqzve86eyb">
            <p className={`text-sm font-bold ${strong}`} data-id="element-3095" data-mp="25oyhhvcuy0ip">{plan.name} plan</p>
            <p className={`text-xs ${muted}`} data-id="element-3096" data-mp="6g1dd8n28tnk">
              {formatCurrency(plan.price)}/mo · {plan.minutes.toLocaleString()} min
            </p>
          </div>
          <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${hasTrialOffer ? isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu' : isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-3097" data-mp="3u0sryu7jx8q">
            {hasTrialOffer ? '3 days free · $0 today' : 'Starts today'}
          </span>
        </div>

        {/* Essential disclosures */}
        <section className="mt-5" aria-labelledby="consent-title" data-id="element-3098" data-mp="mrbo58cpy6iw">
          <h2 id="consent-title" className={`text-sm font-bold ${strong}`} data-id="element-3099" data-mp="httf7hypbraw">
            How Vexu handles your calls
          </h2>
          <ul className={`mt-2.5 space-y-2 text-[13px] leading-snug ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3100" data-mp="ct5nsv6k98g5">
            {DISCLOSURES.map(d => <li key={d} className="flex gap-2" data-id="element-3101" data-mp="2xkfwswpo2b66">
                <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-vexu" data-id="element-3102" data-mp="2xm932awdatdn" />
                {d}
              </li>)}
          </ul>
          <button type="button" onClick={() => setShowDetails(true)} className={`mt-3 inline-flex items-center gap-0.5 text-xs ${link}`} data-id="element-3103" data-mp="lao5tj6tra2c">
            See full details <ChevronRight className="h-3.5 w-3.5" data-id="element-3104" data-mp="bcgsrbfnpc5m" />
          </button>
        </section>

        {/* Carrier availability notice */}
        <p className={`mt-5 flex gap-2 rounded-xl px-3.5 py-3 text-xs leading-relaxed ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/5 text-zinc-400'}`} data-id="element-3105" data-mp="gcct90p5algf">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" data-id="element-3106" data-mp="1908qc6immfdq" />
          Call forwarding depends on your mobile carrier and plan. We'll guide you through setup after payment.
        </p>

        <div role="checkbox" aria-checked={hasAgreed} tabIndex={0} onClick={() => setHasAgreed(v => !v)} onKeyDown={e => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          setHasAgreed(v => !v);
        }
      }} className={`mt-4 w-full cursor-pointer rounded-2xl border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${hasAgreed ? 'border-vexu bg-vexu/10' : isLight ? 'border-slate-200 bg-white hover:border-slate-300' : 'border-white/10 bg-white/5 hover:border-white/20'}`} data-id="element-3107" data-mp="z1j9mh2gv0z2">
          <span className="flex items-start gap-3" data-id="element-3108" data-mp="26rduc3bpkl6l">
            <span className={`mt-0.5 grid h-5 w-5 flex-shrink-0 place-items-center rounded-md border-2 ${hasAgreed ? 'border-vexu bg-vexu text-white' : isLight ? 'border-slate-300 bg-white' : 'border-white/30'}`} data-id="element-3109" data-mp="1j4ywypoz67oa">
              {hasAgreed && <Check className="h-3.5 w-3.5" strokeWidth={3} data-id="element-3110" data-mp="1n5isxpxtb58u" />}
            </span>
            <span className={`text-xs leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-3111" data-mp="1n3eeie2aenub">
              I agree to Vexu’s{' '}
              <a href="/terms-of-service" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className={link} data-id="element-3112" data-mp="veuuilniwdyv">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy-policy" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className={link} data-id="element-3113" data-mp="2zhe8jgeowwu1">
                Privacy Policy
              </a>
              , and to Vexu’s AI call-handling and call-forwarding consent.
            </span>
          </span>
        </div>
      </main>

      <div className={`border-t px-6 pb-7 pt-4 ${isLight ? 'border-slate-200 bg-white' : 'border-white/10 bg-black'}`} data-id="element-3114" data-mp="2blil4ur2za6i">
        <Button fullWidth disabled={!hasAgreed} onClick={onContinue} className="py-3.5" data-id="element-3115" data-mp="2gjk4zhwbzdj1">
          Continue to payment
        </Button>
      </div>
      <ConsentInfoSheet open={showDetails} onClose={() => setShowDetails(false)} data-id="element-3116" data-mp="2qi0x4i709y6o" />
    </div>;
}